import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { exchangeCodeForToken } from '@/lib/onshape-auth';

const APP_URL = import.meta.env.VITE_APP_URL || 'https://app.fastenai.dev';

/**
 * Get target origin for postMessage
 * When opened from popup, use '*' to allow cross-origin messaging
 * The receiving end will validate the origin for security
 * @returns {string} Target origin for postMessage
 */
function getTargetOrigin() {
  // If opened from popup (window.opener exists), use '*' to allow cross-origin
  // This allows the website (fastenai.dev) to receive messages from app (app.fastenai.dev)
  // Security: The receiving end (website) will validate the message origin
  if (window.opener) {
    return '*'; // Allow cross-origin when opened from popup
  }
  // If not a popup, use APP_URL (same-origin scenario)
  return APP_URL;
}

/**
 * Onshape OAuth Callback page
 * Receives OAuth code, exchanges for token, and sends to parent via postMessage
 */
export function OnshapeCallback() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');

  useEffect(() => {
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    const targetOrigin = getTargetOrigin();

    if (error) {
      // OAuth error occurred
      setStatus('error');
      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'ONSHAPE_AUTH_ERROR',
            error: error,
          },
          targetOrigin
        );
      }
      // Close pop-up after a delay
      setTimeout(() => window.close(), 2000);
      return;
    }

    if (!code) {
      setStatus('error');
      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'ONSHAPE_AUTH_ERROR',
            error: 'No authorization code received',
          },
          targetOrigin
        );
      }
      setTimeout(() => window.close(), 2000);
      return;
    }

    // Exchange code for token
    // Cookie is automatically set by Cloud Function (HttpOnly, secure)
    exchangeCodeForToken(code)
      .then((response) => {
        if (!response.success) {
          throw new Error(response.error || 'Authentication failed');
        }

        // Send success message to parent window
        // Store access token for Authorization header (works better with Firebase Hosting rewrites)
        // Store user info in sessionStorage as backup (works even if cookie isn't accessible)
        if (response.access_token) {
          sessionStorage.setItem('fastenai_access_token', response.access_token);
        }
        if (response.refresh_token) {
          sessionStorage.setItem('fastenai_refresh_token', response.refresh_token);
        }
        if (response.user) {
          sessionStorage.setItem('fastenai_user_info', JSON.stringify(response.user));
        }
        
        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'ONSHAPE_AUTH_SUCCESS',
              user: response.user,
              access_token: response.access_token, // Include token so parent can store it
              refresh_token: response.refresh_token, // Include refresh token so parent can store it
            },
            targetOrigin
          );
        } else {
          // Store access token, refresh token, and user info in sessionStorage
          if (response.access_token) {
            sessionStorage.setItem('fastenai_access_token', response.access_token);
          }
          if (response.refresh_token) {
            sessionStorage.setItem('fastenai_refresh_token', response.refresh_token);
          }
          if (response.user) {
            sessionStorage.setItem('fastenai_user_info', JSON.stringify(response.user));
          }
          // If no popup, redirect to main page with auth success flag
          window.location.href = `${APP_URL}/?auth=success`;
        }

        setStatus('success');
        // Close pop-up after a short delay
        setTimeout(() => window.close(), 1000);
      })
      .catch((err) => {
        console.error('Token exchange error:', err);
        setStatus('error');
        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'ONSHAPE_AUTH_ERROR',
              error: err.message || 'Failed to exchange code for token',
            },
            targetOrigin
          );
        }
        setTimeout(() => window.close(), 2000);
      });
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground">Completing authentication...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="text-green-500 text-2xl">✓</div>
            <p className="text-muted-foreground">Authentication successful! Closing window...</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="text-red-500 text-2xl">✗</div>
            <p className="text-muted-foreground">Authentication failed. Closing window...</p>
          </>
        )}
      </div>
    </div>
  );
}

